import type { Angabe, AngabeStand, ProjektSteckbrief } from '../../services/projektSteckbrief';
import type { Bauordnung, ErlassenderRolle } from './types';

/**
 * Vorschlaege fuer die Grundlagen der Baustellenordnung — aus dem
 * Projektsteckbrief (Plan Stufe 3.5, Spezifikation D).
 *
 *   Erlassender  ← Bauherr bzw. verantwortlicher Dritter nach § 4 BaustellV
 *   Geltung      ← Ausfuehrungszeitraum (Entscheid E1; Rueckfall Projektlaufzeit
 *                  bleibt als `ungeklaert` sichtbar)
 *
 * ═══ VORSCHLAG, NIE EINSCHREIBUNG ═════════════════════════════════════════
 * Diese Datei schreibt nichts und entscheidet nichts. Sie sagt, was der
 * Steckbrief hergibt und wie der Kopfsatz aussaehe, wenn jemand uebernimmt.
 * Geschrieben wird erst auf Klick in der Leiste (Falle 5 der Spezifikation:
 * „Abgeleitete Werte werden angezeigt und angeboten, nie eingeschrieben").
 *
 * ═══ WARUM ZWEI ERLASSENDE ZUR WAHL STEHEN ════════════════════════════════
 * Erlassender ist der Auftraggeber — der Bauherr oder der Dritte, der nach
 * § 4 BaustellV die Bauherrenpflichten uebernommen hat (etwa ein GU). Welcher
 * von beiden das Hausrecht ausuebt, ist eine Vertragsfrage, die nur ein Mensch
 * beantwortet. Deshalb werden BEIDE angeboten, nicht einer vorgezogen.
 *
 * Rein — kein DOM, keine Supabase. Der Steckbrief kommt vom Aufrufer.
 */

export type VorschlagsFeld = 'erlassender' | 'geltung_von' | 'geltung_bis';

export interface GrundlagenVorschlag {
    id: 'erlassender_bauherr' | 'erlassender_dritter' | 'geltung_von' | 'geltung_bis';
    feld: VorschlagsFeld;
    /** Beschriftung des Zielfelds. */
    label: string;
    /** Der Wert, lesbar — so steht er in der Leiste. */
    wertText: string;
    quelle: string;
    stand: AngabeStand;
    /** Warum nicht `belegt` — Pflicht bei `ungeklaert`/`widerspruch`. */
    grund?: string;
    /** Was die Uebernahme am Kopfsatz aendert. */
    patch: Partial<Bauordnung>;
    /** Der aktuelle Wert am Kopfsatz, den die Uebernahme ersetzen wuerde — leer, wenn das Feld frei ist. */
    ersetzt: string;
}

/** ISO-Tag → TT.MM.JJJJ durch Zerlegen; sonst wortgetreu. */
const tagDeutsch = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

/**
 * Rolle des Erlassenden aus der Freitext-Rolle des Dritten.
 *
 * Nur, was sich eindeutig lesen laesst: „Generalunternehmer" → `gu`,
 * „Bauleitung" → `bauleitung`. Alles andere bleibt `null` — die Rolle setzt
 * dann der Anwender; ein geratener Wert stuende als Tatsache im Dokument.
 */
export const rolleAusText = (rolle: string | null | undefined): ErlassenderRolle | null => {
    // Wortweise statt mit `\b`: Umlaute zaehlen in JavaScript-Mustern nicht als
    // Wortzeichen, „GÜ" faende sonst keine Wortgrenze.
    const woerter = (rolle || '').toLowerCase().split(/[^a-zäöüß]+/).filter(Boolean);
    if (!woerter.length) return null;
    const kurz = new Set(['gu', 'gü', 'gue', 'tu']);
    if (woerter.some(w => kurz.has(w) || w.startsWith('general') || w.startsWith('total'))) return 'gu';
    if (woerter.some(w => w.startsWith('bauleit') || w.startsWith('bauüberwach') || w.startsWith('bauueberwach'))) {
        return 'bauleitung';
    }
    return null;
};

const wert = <T>(a: Angabe<T>): T | null => a.wert;

/**
 * Die Vorschlaege zum Kopfsatz — nur, wo der Steckbrief einen Wert hat und
 * der Kopfsatz nicht schon genau diesen Wert traegt.
 *
 * Ein abweichend GEFUELLTES Feld ergibt weiterhin einen Vorschlag (mit
 * `ersetzt`): die Leiste zeigt den Unterschied, der Anwender entscheidet.
 * Ein Feld, das den Wert schon traegt, ergibt keinen — sonst stuende die
 * Leiste nie leer.
 */
export const grundlagenVorschlaege = (
    sb: ProjektSteckbrief | null | undefined,
    o: Bauordnung | null | undefined,
): GrundlagenVorschlag[] => {
    if (!sb || !o) return [];
    const liste: GrundlagenVorschlag[] = [];
    const firma = o.erlassender_firma.trim();

    // ─── Erlassender: Bauherr ────────────────────────────────────────────
    const bauherr = wert(sb.bauherr.name);
    if (bauherr && !(firma === bauherr && o.erlassender_rolle === 'bauherr')) {
        liste.push({
            id: 'erlassender_bauherr',
            feld: 'erlassender',
            label: 'Erlassender (Bauherr)',
            wertText: bauherr,
            quelle: sb.bauherr.name.quelle,
            stand: sb.bauherr.name.stand,
            grund: sb.bauherr.name.grund,
            patch: { erlassender_firma: bauherr, erlassender_rolle: 'bauherr' },
            ersetzt: firma,
        });
    }

    // ─── Erlassender: Dritter nach § 4 ───────────────────────────────────
    const dritter = wert(sb.dritter.name);
    if (dritter) {
        const rolleText = wert(sb.dritter.rolle);
        const rolle = rolleAusText(rolleText);
        const schonSo = firma === dritter && (rolle === null || o.erlassender_rolle === rolle);
        if (!schonSo) {
            const patch: Partial<Bauordnung> = { erlassender_firma: dritter };
            if (rolle) patch.erlassender_rolle = rolle;
            liste.push({
                id: 'erlassender_dritter',
                feld: 'erlassender',
                label: 'Erlassender (Dritter nach § 4 BaustellV)',
                wertText: rolleText ? `${dritter} — ${rolleText}` : dritter,
                quelle: sb.dritter.name.quelle,
                stand: sb.dritter.name.stand,
                grund: sb.dritter.name.grund,
                patch,
                ersetzt: firma,
            });
        }
    }

    // ─── Geltung aus dem Ausfuehrungszeitraum (E1) ───────────────────────
    const geltung = (
        id: 'geltung_von' | 'geltung_bis',
        label: string,
        angabe: Angabe<string>,
        aktuell: string | null,
        feld: 'geltung_von' | 'geltung_bis',
    ) => {
        const tag = wert(angabe);
        if (!tag || tag === aktuell) return;
        liste.push({
            id,
            feld,
            label,
            wertText: tagDeutsch(tag),
            quelle: angabe.quelle,
            stand: angabe.stand,
            grund: angabe.grund,
            patch: { [feld]: tag },
            ersetzt: aktuell ? tagDeutsch(aktuell) : '',
        });
    };
    geltung('geltung_von', 'Gültig von', sb.ausfuehrung.beginn, o.geltung_von, 'geltung_von');
    geltung('geltung_bis', 'Gültig bis', sb.ausfuehrung.ende, o.geltung_bis, 'geltung_bis');

    return liste;
};
